import React, { useState, useEffect } from 'react';
import { Beaker, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'labs_welcome_dismissed';

interface LabsWelcomeAlertProps {
  className?: string;
}

export const LabsWelcomeAlert: React.FC<LabsWelcomeAlertProps> = ({ className }) => {
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    setIsDismissed(dismissed === 'true');
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Alert className={`bg-secondary/10 border-secondary/30 relative ${className || ''}`}>
      <Beaker className="h-5 w-5 text-secondary" />
      <div className="pr-8">
        <p className="font-heading font-bold text-foreground mb-1">
          Welcome to Labs! 🧪
        </p>
        <AlertDescription className="text-muted-foreground text-sm leading-relaxed">
          This is where we test new features to see if they have staying power. We know some features 
          need traction to truly shine (more people using them = better results), but we've built in 
          ways to help you get noticed in the meantime—so they're valuable from day one.
          <br /><br />
          <strong className="text-foreground">All Labs features are free to try and will be free forever for those who help us test them.</strong>{' '}
          Eventually, these features will move behind a paywall—so get in now while it's free!
        </AlertDescription>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-6 w-6 text-muted-foreground hover:text-foreground"
        onClick={handleDismiss}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Dismiss</span>
      </Button>
    </Alert>
  );
};
